import type { AppConfig } from "../../types/appConfig";
import type { PaginatedResponse } from "../zekay/pagination";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";
import type {
  LoginForm,
  User,
  UserWithRole,
  UpdateUserProfilePayload,
  CreateDirectoryUserPayload,
  ActivateUserAccountPayload,
  UserDocument,
  UserDocumentPatchPayload,
  UserDocumentsBatchItem,
  UserDocumentsAlertBatchItem,
  UserDocumentsAlertBatchResponseItem,
  UserDisplayBatchItem,
} from "./User.typed";
import type { Role } from "../roles/Role.typed";

function parseWarnBeforeNs(raw: Record<string, unknown>): number | null {
  const v = raw.warn_before;
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeExpiresAt(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  if (s.startsWith("0001-01-01")) return null;
  return s;
}

function parseUserDocument(raw: Record<string, unknown>): UserDocument {
  const id = Number(raw.id);
  const userId = Number(raw.user_id);
  const companyId = Number(raw.company_id);
  return {
    id: Number.isFinite(id) ? id : 0,
    user_id: Number.isFinite(userId) ? userId : 0,
    company_id: Number.isFinite(companyId) ? companyId : 0,
    name: typeof raw.name === "string" ? raw.name : "",
    url: typeof raw.url === "string" ? raw.url : "",
    created_at: typeof raw.created_at === "string" ? raw.created_at : "",
    updated_at: typeof raw.updated_at === "string" ? raw.updated_at : "",
    expires_at: normalizeExpiresAt(raw.expires_at),
    warn_before: parseWarnBeforeNs(raw),
  };
}

function normalizeWireRole(raw: unknown): Role | null {
  if (raw == null || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  const name = typeof r.name === "string" ? r.name : "";
  const pl = Array.isArray(r.permission_list)
    ? r.permission_list
    : Array.isArray(r.permissionList)
      ? r.permissionList
      : [];
  const permission_list = pl.filter((x): x is string => typeof x === "string");
  const is_employee =
    typeof r.is_employee === "boolean"
      ? r.is_employee
      : typeof r.isEmployee === "boolean"
        ? r.isEmployee
        : false;
  const cid = r.company_id;
  const company_id = cid != null && Number.isFinite(Number(cid)) ? Number(cid) : undefined;
  const out: Role = {
    id: Number.isFinite(id) ? id : 0,
    name,
    permission_list: permission_list as Role["permission_list"],
    is_employee,
  };
  if (company_id !== undefined) out.company_id = company_id;
  return out;
}

function parseUserDirectoryEntry(entry: unknown): UserWithRole {
  const item = (entry ?? {}) as Record<string, unknown>;
  const nested = item.user;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const role = normalizeWireRole(item.role);
    return {
      user: nested as unknown as User,
      role,
    };
  }
  const role = normalizeWireRole(item.role);
  const rest = { ...item };
  delete rest.role;
  return {
    user: rest as unknown as User,
    role,
  };
}

function extractListPayload(json: unknown): unknown[] {
  if (Array.isArray(json)) return json;
  if (json && typeof json === "object" && Array.isArray((json as { data?: unknown }).data)) {
    return (json as { data: unknown[] }).data;
  }
  return [];
}

function parsePaginatedUserDocuments(
  json: unknown,
  requestedPage: number,
  requestedLimit: number,
): PaginatedResponse<UserDocument> {
  const data = extractListPayload(json).map((item) =>
    parseUserDocument(item as Record<string, unknown>),
  );
  if (Array.isArray(json)) {
    return {
      data,
      total: data.length,
      page: 1,
      limit: requestedLimit,
      total_pages: Math.max(1, Math.ceil(data.length / requestedLimit) || 1),
    };
  }
  const o = json && typeof json === "object" ? (json as Record<string, unknown>) : {};
  const total = Number(o.total ?? data.length);
  const page = Number(o.page ?? requestedPage);
  const limit = Number(o.limit ?? requestedLimit);
  const total_pages = Number(
    o.total_pages ?? (limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1),
  );
  return { data, total, page, limit, total_pages };
}

export class UserModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async getUsers(employeesOnly = false): Promise<UserWithRole[]> {
    const response = await fetchZekay(
      this.zekayAPI + "/users" + (employeesOnly ? "?employees=true" : ""),
    );
    await assertResponseOk(response);
    const json: unknown = await response.json();
    const raw = Array.isArray(json) ? json : [];
    return raw.map((entry: unknown) => parseUserDirectoryEntry(entry));
  }

  async getUsersDisplayBatch(ids: number[]): Promise<UserDisplayBatchItem[]> {
    if (ids.length === 0) return [];
    const response = await fetchZekay(`${this.zekayAPI}/users/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
    await assertResponseOk(response);
    const json: unknown = await response.json().catch(() => null);
    if (!Array.isArray(json)) return [];
    return json.map((row) => {
      const r = (row ?? {}) as Record<string, unknown>;
      const q = r.last_qualification;
      const bid = Number(r.id);
      return {
        id: Number.isFinite(bid) ? bid : 0,
        first_name: typeof r.first_name === "string" ? r.first_name : "",
        last_name: typeof r.last_name === "string" ? r.last_name : "",
        last_qualification: typeof q === "string" && q.trim() !== "" ? q : undefined,
      } as UserDisplayBatchItem;
    });
  }

  async createDirectoryUser(payload: CreateDirectoryUserPayload): Promise<User> {
    const response = await fetchZekay(this.zekayAPI + "/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        first_name: payload.first_name,
        last_name: payload.last_name,
        phone_number: payload.phone_number ?? "",
        external_id: payload.external_id ?? "",
        is_guide: payload.is_guide ?? false,
        ...(payload.email ? { email: payload.email } : {}),
        ...(payload.role_id ? { role_id: payload.role_id } : {}),
      }),
    });
    await expectStatuses(response, 201);
    return (await response.json()) as User;
  }

  async prepareUserActivation(id: number, payload: ActivateUserAccountPayload): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + `/users/${id}/activation`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: payload.email,
        role_id: payload.role_id ?? 0,
      }),
    });
    await expectStatuses(response, 204);
  }

  async sendUserInvitationMail(id: number): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + `/users/${id}/mails`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event: "invitation" }),
    });
    await expectStatuses(response, 204);
  }

  async createInvitedUser(form: LoginForm): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/users/register/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    await expectStatuses(response, 204);
  }

  async deleteUser(id: number): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + `/users/${id}`, { method: "DELETE" });
    await expectStatuses(response, 204);
  }

  async updateUserRole(id: number, roleId: number): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + `/users/${id}/role`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role_id: roleId }),
    });
    await expectStatuses(response, 204);
  }

  async updateUserProfile(id: number, payload: UpdateUserProfilePayload): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + `/users/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        first_name: payload.first_name,
        last_name: payload.last_name,
        last_qualification: payload.last_qualification ?? "",
        phone_number: payload.phone_number ?? "",
        is_guide: payload.is_guide ?? false,
        ...(payload.external_id !== undefined ? { external_id: payload.external_id } : {}),
      }),
    });
    await expectStatuses(response, 204);
  }

  async getUserDocuments(
    userId: number,
    params?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<UserDocument>> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    const response = await fetchZekay(`${this.zekayAPI}/users/${userId}/documents?${qs}`);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) {
      return { data: [], total: 0, page, limit, total_pages: 0 };
    }
    const json = await response.json();
    return parsePaginatedUserDocuments(json, page, limit);
  }

  async getUserDocumentsBatch(
    items: UserDocumentsBatchItem[],
  ): Promise<PaginatedResponse<UserDocument>[]> {
    if (items.length === 0) return [];
    if (items.length > 20) {
      throw new Error("Le lot documents utilisateur est limité à 20 entrées");
    }
    const body = items.map((it) => ({
      user_id: it.user_id,
      page: it.page ?? 1,
      limit: it.limit ?? 10,
    }));
    const response = await fetchZekay(`${this.zekayAPI}/users/documents/all`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await assertResponseOk(response);
    const json: unknown = await response.json();
    if (!Array.isArray(json)) {
      throw new Error("Réponse batch documents invalide");
    }
    return (json as unknown[]).map((entry, idx) => {
      const it = items[idx];
      const p = it.page ?? 1;
      const l = it.limit ?? 10;
      return parsePaginatedUserDocuments(entry, p, l);
    });
  }

  async getUserDocumentsAlertBatch(
    items: UserDocumentsAlertBatchItem[],
  ): Promise<UserDocumentsAlertBatchResponseItem[]> {
    if (items.length === 0) return [];
    if (items.length > 20) {
      throw new Error("Le lot alertes documents utilisateur est limité à 20 entrées");
    }
    const response = await fetchZekay(`${this.zekayAPI}/users/documents/alert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(items),
    });
    await assertResponseOk(response);
    const json: unknown = await response.json();
    if (!Array.isArray(json)) {
      throw new Error("Réponse batch alert documents invalide");
    }
    return (json as unknown[]).map((entry) => {
      const raw = (entry ?? {}) as Record<string, unknown>;
      const docs = Array.isArray(raw.documents) ? raw.documents : [];
      return {
        user_id: (() => {
          const n = Number(raw.user_id);
          return Number.isFinite(n) ? n : 0;
        })(),
        user_name: typeof raw.user_name === "string" ? raw.user_name : undefined,
        alerts_count:
          raw.alerts_count != null && Number.isFinite(Number(raw.alerts_count))
            ? Number(raw.alerts_count)
            : undefined,
        documents: docs.map((doc) => parseUserDocument(doc as Record<string, unknown>)),
      } as UserDocumentsAlertBatchResponseItem;
    });
  }

  async uploadUserDocument(userId: number, formData: FormData): Promise<UserDocument> {
    const response = await fetchZekay(this.zekayAPI + `/users/${userId}/documents`, {
      method: "POST",
      body: formData,
    });
    await expectStatuses(response, 201);
    const json = await response.json();
    return parseUserDocument(json as Record<string, unknown>);
  }

  async patchUserDocument(
    userId: number,
    documentId: number,
    payload: UserDocumentPatchPayload,
  ): Promise<UserDocument> {
    const body: Record<string, unknown> = {};
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.expires_at !== undefined) body.expires_at = payload.expires_at;
    if (payload.warn_before !== undefined) body.warn_before = payload.warn_before;

    const response = await fetchZekay(this.zekayAPI + `/users/${userId}/documents/${documentId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await assertResponseOk(response);
    const json = await response.json();
    return parseUserDocument(json as Record<string, unknown>);
  }

  async deleteUserDocument(userId: number, documentId: number): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + `/users/${userId}/documents/${documentId}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }
}
